/**
 * The arithmetic of a Pareto chart.
 *
 * A Pareto chart is a bar chart of counts sorted from the largest to the smallest, with a
 * line of the running total as a share of the whole. Reading across it answers "how many
 * of the categories account for most of the total?" — the "vital few" that the 80% line
 * picks out.
 *
 * Choices made here, each of which a reader can see in the result:
 *
 *   - Entries with the same label are added together (a raw tally pasted from a
 *     spreadsheet often repeats a label); negative and non-numeric values cannot be a
 *     part of a whole and are left out, and counted so the widget can say so.
 *   - Ties keep the order they were entered in.
 *   - "Other" always comes last, however large it is — that is the convention, and it
 *     keeps the named categories in descending order. The running total passes through
 *     it and ends at 100%.
 *   - The vital few are the categories up to and including the first one at which the
 *     running total reaches the threshold, so the shaded bars always cover at least 80%.
 */

export interface ParetoEntry {
  label: string;
  value: number;
}

export type OtherMode = "none" | "top" | "below";

export interface ParetoRow {
  label: string;
  value: number;
  /** This row's share of the total, 0–1. */
  share: number;
  /** The running total through this row as a share of the whole, 0–1. */
  cumulative: number;
  isOther: boolean;
}

export interface ParetoResult {
  rows: ParetoRow[];
  total: number;
  /** How many rows it takes to reach the threshold; 0 when the total is 0. */
  vitalFew: number;
  /** How many categories were folded into "Other" (0 when there is no such row). */
  grouped: number;
  /** Entries added into an earlier entry with the same label. */
  merged: number;
  /** Entries left out for being negative. */
  negative: number;
}

export interface ParetoOptions {
  otherMode: OtherMode;
  /** For `top`: how many categories to keep. For `below`: a percentage of the total. */
  otherLimit: number | null;
  /** The share the vital few must reach, 0–1. Default 0.8. */
  threshold?: number;
  /** The name of the combined row. */
  otherLabel: string;
}

function mergeEntries(entries: readonly ParetoEntry[]): {
  items: ParetoEntry[];
  merged: number;
  negative: number;
} {
  const byLabel = new Map<string, number>();
  let merged = 0;
  let negative = 0;
  for (const entry of entries) {
    if (!Number.isFinite(entry.value) || entry.value < 0) {
      negative += 1;
      continue;
    }
    const label = entry.label.trim();
    if (byLabel.has(label)) {
      merged += 1;
    }
    byLabel.set(label, (byLabel.get(label) ?? 0) + entry.value);
  }
  const items = [...byLabel.entries()].map(([label, value]) => ({
    label,
    value,
  }));
  return { items, merged, negative };
}

/** Which of the sorted items go into "Other". */
function groupedCount(
  sorted: readonly ParetoEntry[],
  total: number,
  { otherMode, otherLimit }: ParetoOptions
): number {
  if (otherMode === "none" || otherLimit === null || otherLimit < 0) {
    return 0;
  }
  let keep = sorted.length;
  if (otherMode === "top") {
    keep = Math.max(1, Math.floor(otherLimit));
  } else if (total > 0) {
    keep = sorted.filter(
      (item) => (item.value / total) * 100 >= otherLimit
    ).length;
  }
  const rest = sorted.length - keep;
  // Folding a single category into "Other" would only rename it.
  return rest >= 2 ? rest : 0;
}

export function buildPareto(
  entries: readonly ParetoEntry[],
  options: ParetoOptions
): ParetoResult {
  const { items, merged, negative } = mergeEntries(entries);
  const sorted = items
    .map((item, index) => ({ ...item, index }))
    .sort((a, b) => b.value - a.value || a.index - b.index);
  const total = sorted.reduce((sum, item) => sum + item.value, 0);
  const grouped = groupedCount(sorted, total, options);
  const kept = sorted.slice(0, sorted.length - grouped);
  const display: { label: string; value: number; isOther: boolean }[] =
    kept.map((item) => ({ ...item, isOther: false }));
  if (grouped > 0) {
    const otherValue = sorted
      .slice(sorted.length - grouped)
      .reduce((sum, item) => sum + item.value, 0);
    display.push({
      label: options.otherLabel,
      value: otherValue,
      isOther: true,
    });
  }
  const threshold = options.threshold ?? 0.8;
  let running = 0;
  let vitalFew = 0;
  const rows = display.map((item, index): ParetoRow => {
    running += item.value;
    const cumulative = total > 0 ? running / total : 0;
    if (vitalFew === 0 && total > 0 && cumulative >= threshold - 1e-9) {
      vitalFew = index + 1;
    }
    return {
      ...item,
      share: total > 0 ? item.value / total : 0,
      cumulative,
    };
  });
  return { rows, total, vitalFew, grouped, merged, negative };
}
